<template>
  <table class="filelist" style="width: 100%;">
    <thead>
      <tr>
        <th colspan="2"></th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <th>{{ LANG.title }}</th>
        <td>
          <label class="oce_label" :title="info.title">{{ info.title }}</label>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.nodeId }}</th>
        <td>
          <label class="oce_label" :title="info.node_id">{{ info.node_id }}</label>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.directory }}</th>
        <td>
          <label class="oce_label" :title="info.directory">{{ info.directory }}</label>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.filename }}</th>
        <td>
          <label class="oce_label" :title="info.filename">{{ info.filename }}</label>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.uri }}</th>
        <td>
          <a :href="info.uri" target="_blank">
            <span :title="info.uri">{{ info.uri }}</span>
          </a>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.template }}</th>
        <td>
          <label class="oce_label" :title="info.template">{{ info.template }}</label>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.metaPage }}</th>
        <td>
          <label class="oce_label" :title="info.metapage">{{ info.metapage }}</label>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.status }}</th>
        <td>
          <UpToDate :onLive="info.on_live" :publishDate="variantsStatus.publishScheduled" :outdated="variantsStatus.outdated" :nodeId="info.node_id" :publishUri="'_all_'" :canReimport="true"/>
        </td>
      </tr>
      <tr>
        <th>{{ variants.label }}</th>
        <td>
          <Variants :item="variants.data" :nodeId="info.node_id"/>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.publishingDate }}</th>
        <td>
           <label class="oce_label" :title="publishDate">{{ publishDate }}</label>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.expiryDate }}</th>
        <td>
           <label class="oce_label" :title="expiryDate">{{ expiryDate }}</label>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.workflowStatus }}</th>
        <td>
           <label class="oce_label" :title="workflowStatus">{{ workflowStatus }}</label>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.publishingState }}</th>
        <td>
           <label class="oce_label" :title="publishState">{{ publishState }}</label>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.publishedPagesAndServers }}</th>
        <td>
            <PublishedPages :pages="info.published_to_live" :targets="info.targets"/>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.references }}</th>
        <td>
            <References :item="info.references" :nodeId="info.node_id"/>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.usedBy }}</th>
        <td>
            <UsedBy :item="info.references" :nodeId="info.node_id"/>
        </td>
      </tr>
      <tr>
        <th>{{ LANG.metaInfo }}</th>
        <td>
          <a :href="uri('/document/oce/meta', { __imperia_node_id: info.node_id, directory: info.directory, filename: info.filename })" target="_blank">
            {{ LANG.showEntireList }}
          </a>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import { defineComponent } from 'vue';
import { mapState } from 'vuex';

import fetchService from '@/utils/model/fetch_service';
import LANG from '@/lang/eng';
import globals from '@/globals';

import UpToDate from '@/components/browser/views/details/UpToDate.vue';
import Variants from '@/components/browser/views/details/Variants.vue';
import References from '@/components/browser/views/details/References.vue';
import UsedBy from '@/components/browser/views/details/UsedBy.vue';
import PublishedPages from '@/components/browser/views/details/PublishedPages.vue';

const { uri } = globals.I.Util;

export default defineComponent({
  components: {
    UpToDate,
    Variants,
    References,
    UsedBy,
    PublishedPages,
  },
  props: ['nodeId'],
  data: () => ({
    info: Object,
  }),
  async created() {
    const params = {
      __imperia_node_id: this.nodeId,
    };
    const response = await fetchService.GetData('docbrowser_info_panel', params);

    this.info = response.data;
  },
  computed: {
    ...mapState(['LANG']),
    keys() {
      return Object.keys(this.info);
    },
    publishDate() {
      if (this.info.publish_date === null) return LANG.manually;

      return this.info.publish_date;
    },
    expiryDate() {
      if (this.info.expiry_date === null) return LANG.manually;

      return this.info.expiry_date;
    },
    workflowStatus() {
      if (this.info.in_workflow) return LANG.inWorkflow;

      return LANG.inArchive;
    },
    publishState() {
      if (this.info.on_live) return LANG.publishedToLiveServer;

      return LANG.notPublished;
    },
    variants() {
      const isCopyPages = this.info.copy_pages && Object.keys(this.info.copy_pages).length > 0;
      const label = isCopyPages ? this.LANG.generatedPages : this.LANG.variants;
      const data = isCopyPages ? this.info.copy_pages : this.info.variants;

      return { label, data };
    },
    variantsStatus() {
      const isCopyPages = this.info.copy_pages && Object.keys(this.info.copy_pages).length > 0;
      const data = isCopyPages ? this.info.copy_pages : this.info.variants;
      if (data && Object.keys(data).length > 0) {
        let hasOutdated = false;
        let hasScheduled = false;

        Object.values(data).forEach((copyPage) => {
          if (copyPage.on_live && copyPage.outdated) {
            hasOutdated = true;
          }
          if (copyPage.on_live && copyPage.publish_date) {
            hasScheduled = true;
          }
        });

        return {
          outdated: hasOutdated,
          publishScheduled: hasScheduled,
        };
      }

      return {
        outdated: false,
        publishScheduled: false,
      };
    },
  },
  methods: {
    uri,
  },
});
</script>

<style scoped lang='scss'>
.filelist {
  thead {
    position: relative;
    top: 0px;
    box-shadow: none;

    th {
      height: 5px;
    }

    tr {
      border-bottom: 3px solid #cfcfcf;
    }
  }
  th {
    font-family: $font-family-regular;
    font-weight: 500px;
    font-size: 13px;
    vertical-align: top;
  }
  th, td {
    padding: 0px;
    white-space: nowrap;
    height: 40px;
    text-align: left;
  }
  td, td label {
    font-family: $font-family-light;
    font-weight: 400px;
    font-size: 13px;
  }
  td a {
    color: #1c519c;
    text-decoration: none;
  }
}
</style>
